import clsx from 'clsx';
import { Gamepad2, Hand, Loader2, Volume2, VolumeX } from 'lucide-react';
import { motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { api, ApiError, newKey } from '../../../lib/api';
import type { LessonPayload } from '../../../lib/types';
import { Button, Card, Notice } from '../../../ui';

interface Props {
  lesson: LessonPayload;
  onFinished: (revision: number) => void;
}

interface GameComplete {
  type: 'game:complete';
  gameId: string;
  stars: number;
  maxStars: number;
  items: Array<Record<string, unknown>>;
}

/**
 * Hosts the lesson game in an iframe and listens to the game bridge
 * (game:ready / progress / complete / exit / error). Only messages from our
 * own origin and from this iframe are accepted.
 */
export function LabStage({ lesson, onFinished }: Props) {
  const [tab, setTab] = useState<'game' | 'unplugged'>('game');
  const [status, setStatus] = useState<'loading' | 'ready' | 'saving' | 'error'>('loading');
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState('');
  const frame = useRef<HTMLIFrameElement>(null);
  const saved = useRef(false);
  const key = useRef(newKey());

  useEffect(() => {
    const onMessage = async (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.source !== frame.current?.contentWindow) return;
      const msg = e.data as { type?: string } | null;
      if (!msg || typeof msg !== 'object') return;
      if (msg.type === 'game:ready') setStatus('ready');
      if (msg.type === 'game:error') setStatus((s) => (s === 'loading' ? 'error' : s));
      if (msg.type === 'game:complete' && !saved.current) {
        const m = msg as GameComplete;
        saved.current = true;
        setStatus('saving');
        try {
          const r = await api.post<{ revision: number }>(`/student/lessons/${lesson.lesson.id}/game-result`, {
            idempotencyKey: key.current,
            gameId: m.gameId,
            stars: m.stars,
            maxStars: m.maxStars,
            items: m.items,
          });
          onFinished(r.revision);
        } catch (err) {
          saved.current = false;
          setStatus('ready');
          setError(err instanceof ApiError ? err.message : 'We could not save your game. Please try again.');
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [lesson.lesson.id, onFinished]);

  // If the game never says it is ready, show a friendly error instead of a blank box.
  useEffect(() => {
    if (status !== 'loading') return;
    const t = setTimeout(() => setStatus((s) => (s === 'loading' ? 'error' : s)), 15_000);
    return () => clearTimeout(t);
  }, [status]);

  const send = (msg: Record<string, unknown>) => frame.current?.contentWindow?.postMessage(msg, window.location.origin);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div role="tablist" className="inline-flex rounded-2xl bg-line/60 p-1.5">
          {([
            ['game', lesson.lab.title, 'Play', Gamepad2],
            ['unplugged', 'Try it without a screen', 'No screen', Hand],
          ] as const).map(([v, label, short, Icon]) => (
            <button
              key={v}
              role="tab"
              aria-selected={tab === v}
              onClick={() => setTab(v)}
              className={clsx('flex h-11 items-center gap-2 whitespace-nowrap rounded-xl px-4 font-semibold', tab === v ? 'bg-surface shadow-sm' : 'text-muted hover:text-ink')}
            >
              <Icon className="size-4" aria-hidden />
              <span className="sm:hidden">{short}</span>
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
        {tab === 'game' && status === 'ready' && (
          <Button
            variant="secondary"
            onClick={() => { send({ type: 'mute', value: !muted }); setMuted(!muted); }}
            icon={muted ? <VolumeX className="size-4" aria-hidden /> : <Volume2 className="size-4" aria-hidden />}
          >
            {muted ? 'Sound off' : 'Sound on'}
          </Button>
        )}
      </div>

      {error && <Notice tone="error">{error}</Notice>}

      <div className={clsx(tab !== 'game' && 'hidden')}>
        <div className="relative overflow-hidden rounded-[var(--radius-card)] bg-surface shadow-[var(--shadow-lift)]">
          <iframe
            ref={frame}
            title={lesson.lab.title}
            src={lesson.lab.url}
            // Phones: fill the screen height so the whole game fits. Larger screens: 16:9.
            className="h-[calc(100dvh-9rem)] min-h-[480px] w-full sm:h-auto sm:min-h-0 sm:aspect-video"
            allow="autoplay"
            sandbox="allow-scripts allow-same-origin"
          />
          {(status === 'loading' || status === 'saving') && (
            <div className="absolute inset-0 flex items-center justify-center gap-3 bg-surface/90 font-semibold text-muted">
              <Loader2 className="size-6 animate-spin text-brand" aria-hidden />
              {status === 'saving' ? 'Saving your stars…' : 'Loading the game…'}
            </div>
          )}
          {status === 'error' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-surface p-6 text-center">
              <p className="font-display text-xl font-bold">The game did not load</p>
              <p className="max-w-sm text-muted">Check the internet, then try again. If it keeps happening, tell your teacher.</p>
              <Button onClick={() => { setStatus('loading'); frame.current?.setAttribute('src', lesson.lab.url); }}>Try again</Button>
            </div>
          )}
        </div>
      </div>

      {tab === 'unplugged' && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="p-6 sm:p-8">
            <h3 className="text-2xl font-extrabold">{lesson.lab.unplugged.title}</h3>
            <ol className="mt-5 flex flex-col gap-3">
              {lesson.lab.unplugged.steps.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-lg">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-mint-soft font-display font-bold text-mint">{i + 1}</span>
                  {s}
                </li>
              ))}
            </ol>
            <p className="mt-6 text-sm font-semibold text-muted">You need: {lesson.lab.unplugged.materials.join(', ')}</p>
            <p className="mt-2 text-sm text-muted">Play the game on screen too so your stars are saved.</p>
          </Card>
        </motion.div>
      )}
    </div>
  );
}
